import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Globe, Loader2, BadgeCheck, UploadCloud, Check } from "lucide-react";
import { imgUrl } from "@/lib/format";
import { toast } from "sonner";

const UploadBox = ({ label, hint, testid, url, setUrl, visibility = "public" }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("visibility", visibility);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(res.data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <label data-testid={testid} className="mt-1 flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-xl p-3 cursor-pointer hover:border-navy hover:bg-navy/5 transition-colors">
        {url ? (
          <img src={imgUrl(url)} alt="" className="h-12 w-12 rounded-lg object-cover" />
        ) : (
          <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
            {busy ? <Loader2 className="h-5 w-5 animate-spin text-navy" /> : <UploadCloud className="h-5 w-5 text-slate-500" />}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-700 flex items-center gap-1">{url ? <><Check className="h-4 w-4 text-green-600" /> Uploaded</> : "Tap to upload"}</p>
          <p className="text-[11px] text-slate-400">{hint}</p>
        </div>
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" disabled={busy} />
      </label>
    </div>
  );
};

export const VerifyDialog = ({ trigger, open, onOpenChange }) => {
  const { setUser } = useAuth();
  const [aadhaar, setAadhaar] = useState("");
  const [pan, setPan] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [passport, setPassport] = useState("");
  const [country, setCountry] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const sendOtp = async () => {
    try {
      const res = await api.post("/verify/send-otp");
      setOtpSent(true);
      toast.info(res.data.message || "OTP sent");
    } catch { toast.error("Could not send OTP"); }
  };

  const requirePhotos = () => {
    if (!photoUrl || !docUrl) {
      toast.error("Please upload both your display photo and your document.");
      return false;
    }
    return true;
  };

  const submitResident = async () => {
    if (!requirePhotos()) return;
    setLoading(true);
    try {
      const res = await api.post("/verify/resident", { aadhaar, pan, otp, photo_url: photoUrl, document_url: docUrl });
      setUser(res.data);
      toast.success("Identity verified! You're now a Verified user ✅");
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const submitNri = async () => {
    if (!requirePhotos()) return;
    setLoading(true);
    try {
      const res = await api.post("/verify/nri", { passport, country, photo_url: photoUrl, document_url: docUrl });
      setUser(res.data);
      toast.success("NRI identity verified! 🌍");
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="verify-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-navy" /> Verify your identity
          </DialogTitle>
          <DialogDescription className="sr-only">Complete KYC to get a Verified badge and post properties.</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Verification builds trust so the right person can be found. Demo OTP is <b>123456</b>.</p>

        <Tabs defaultValue="resident" className="mt-2">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="resident" data-testid="verify-tab-resident"><ShieldCheck className="h-4 w-4 mr-1.5" /> Indian Resident</TabsTrigger>
            <TabsTrigger value="nri" data-testid="verify-tab-nri"><Globe className="h-4 w-4 mr-1.5" /> NRI</TabsTrigger>
          </TabsList>

          <TabsContent value="resident" className="space-y-3 mt-4">
            <div>
              <Label className="text-xs">Aadhaar Number</Label>
              <Input data-testid="verify-aadhaar" value={aadhaar} onChange={(e) => setAadhaar(e.target.value)} placeholder="12-digit Aadhaar" maxLength={12} />
            </div>
            <div>
              <Label className="text-xs">PAN Number</Label>
              <Input data-testid="verify-pan" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
            </div>
            <UploadBox label="Your Display Photo" hint="A clear selfie/portrait (real photo of you)" testid="verify-photo" url={photoUrl} setUrl={setPhotoUrl} visibility="private" />
            <UploadBox label="ID Document (Aadhaar/PAN)" hint="Clear photo of your document" testid="verify-document" url={docUrl} setUrl={setDocUrl} visibility="private" />
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">OTP</Label>
                <Input data-testid="verify-otp" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter OTP" maxLength={6} />
              </div>
              <Button type="button" variant="outline" onClick={sendOtp} data-testid="verify-send-otp" className="border-navy text-navy">
                {otpSent ? "Resend" : "Get OTP"}
              </Button>
            </div>
            <Button onClick={submitResident} disabled={loading} data-testid="verify-resident-submit" className="w-full bg-navy hover:bg-navy-dark text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Get Badge"}
            </Button>
          </TabsContent>

          <TabsContent value="nri" className="space-y-3 mt-4">
            <div>
              <Label className="text-xs">Passport Number</Label>
              <Input data-testid="verify-passport" value={passport} onChange={(e) => setPassport(e.target.value.toUpperCase())} placeholder="e.g. M1234567" />
            </div>
            <div>
              <Label className="text-xs">Country of Residence</Label>
              <Input data-testid="verify-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. United States" />
            </div>
            <UploadBox label="Your Display Photo" hint="A clear selfie/portrait (real photo of you)" testid="verify-photo-nri" url={photoUrl} setUrl={setPhotoUrl} visibility="private" />
            <UploadBox label="Passport / OCI Document" hint="Clear photo of your passport or OCI/PIO card" testid="verify-document-nri" url={docUrl} setUrl={setDocUrl} visibility="private" />
            <Button onClick={submitNri} disabled={loading} data-testid="verify-nri-submit" className="w-full bg-navy hover:bg-navy-dark text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify as NRI 🌍"}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
